import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  onAuthStateChanged,
  onIdTokenChanged,
  signOut,
  User,
} from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";

import { auth } from "../lib/firebase";
import { useAppStore } from "../store/app";
import AuthApi from "../api/auth";
import { usePushNotifications } from "./native-push-notification";

type AuthContextType = {
  loading: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  loading: true,
  logout: async () => {},
  refreshUser: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true); // Is firebase auth loading

  const setAppUser = useAppStore((state) => state.setAppUser);
  const cleanAppUser = useAppStore((state) => state.cleanAppUser);
  const setAppLoading = useAppStore((state) => state.setAppLoading);
  const { userPush } = usePushNotifications();

  /*
  const cleanUserAndAuth = async () => {
    cleanAppUser();
    cleanAppAuth();
    await removeAuth();
  };
  */

  const logout = async () => {
    setAppLoading(true);

    const logOutData = { pushToken: userPush.token || "" };
    await AuthApi.logout(logOutData);

    if (Capacitor.isNativePlatform()) {
      await FirebaseAuthentication.signOut();
    }

    await signOut(auth);
    cleanAppUser();
    setAppLoading(false);
    window.location.reload();
  };

  // Flow for handling user state changes
  useEffect(() => {
    async function handleUserChange(u: User | null, isAuthChange = false) {
      //console.log("Auth state changed, user:", u);
      if (u) {
        fetchData();
      } else {
        cleanAppUser();
        setAppLoading(false); // No user, app can render public content
      }
      setLoading(false); // Firebase auth has finished loading
    }
    const sub1 = onAuthStateChanged(auth, (u) => handleUserChange(u, true));
    //const sub2 = onIdTokenChanged(auth, (u) => handleUserChange(u, false));
    return () => {
      sub1();
      //sub2();
    };
  }, []);

  //const fetchData = async () => {};

  const fetchData = async () => {
    const { data, err } = await AuthApi.me();
    if (err) {
      console.warn("Failed to fetch user data:", err);
      //logout();
      return;
    }
    if (data) {
      const { email, company, language, companies, role, dashboard } =
        data.user;
      setAppUser({ email, company, language, companies, role, dashboard });
    }
    setAppLoading(false); // App can render protected content now
  };

  return (
    <AuthContext.Provider
      value={{
        loading,
        logout,
        refreshUser: fetchData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
